import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';
import { bookmarkAPI, userHideAPI } from '../services/api';
import Header from './Header';
import Footer from './Footer';
import BottomNavigationBar from './BottomNavigationBar';
import FollowButton from './UserFollowing/FollowButton';
import UserFollowStats from './UserFollowing/UserFollowStats';
import BookmarkModal from './Talent/BookmarkModal';
import ReportUserModal from './Talent/ReportUserModal';
import ReviewsList from './Reviews/ReviewsList';
import './PublicProfile.css';
import './Jobs/JobDetailPage.css';

const PublicProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkLists, setBookmarkLists] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [unhiding, setUnhiding] = useState(false);

  useEffect(() => {
    const fetchPublicProfile = async () => {
      try {
        setLoading(true);
        console.log('Fetching public profile for username:', username);
        const response = await ApiService.get(`/public-profile/${username}`);
        
        console.log('Public profile API response:', response);
        
        if (response.success) {
          setUser(response.data.user);
          setIsHidden(response.data.user?.is_hidden || false);
          console.log('Public profile user data:', response.data.user);
        } else {
          setError('Profile not found');
        }
      } catch (err) {
        console.error('Error fetching public profile:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    if (username) {
      fetchPublicProfile();
    }
  }, [username]);

  // Check if user is viewing their own profile
  const isOwnProfile = React.useMemo(() => {
    if (!currentUser || !user) return false;
    
    return (
      currentUser.id === user.id ||
      currentUser.email === user.email ||
      (currentUser.username && currentUser.username === user.username)
    );
  }, [currentUser, user]);

  // Check if talent is bookmarked
  useEffect(() => {
    if (user?.id && isAuthenticated && !isOwnProfile) {
      checkBookmarkStatus();
    }
  }, [user?.id, isAuthenticated, isOwnProfile]);

  const checkBookmarkStatus = async () => {
    try {
      const response = await bookmarkAPI.checkBookmarked(user.id);
      if (response.success) {
        setIsBookmarked(response.is_bookmarked);
        setBookmarkLists(response.lists || []);
      }
    } catch (err) {
      console.error('Error checking bookmark status:', err);
    }
  };

  const handleBookmarkClick = () => {
    if (!isOwnProfile && isAuthenticated) {
      setShowBookmarkModal(true);
    }
  };

  const handleBookmarked = (listId) => {
    setIsBookmarked(true);
    checkBookmarkStatus();
  };

  const handleUnhideUser = async () => {
    if (!user?.id) return;
    
    setUnhiding(true);
    try {
      const response = await userHideAPI.unhide(user.id);
      if (response.success) {
        setIsHidden(false);
        // Optionally refresh the page or show a success message
        alert('User has been unhidden and will appear in your listings.');
      } else {
        alert(response.message || 'Failed to unhide user');
      }
    } catch (error) {
      console.error('Error unhiding user:', error);
      alert('Failed to unhide user. Please try again.');
    } finally {
      setUnhiding(false);
    }
  };

  // Helper function to handle navigation with Ctrl/Cmd+Click support
  const handleNavigation = (path, options = {}) => {
    return (e) => {
      // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
      const isModifierPressed = e.ctrlKey || e.metaKey;
      
      if (isModifierPressed) {
        // Open in new tab
        e.preventDefault();
        const url = `${window.location.origin}${path}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        // Normal navigation
        if (options.state) {
          navigate(path, options);
        } else {
          navigate(path);
        }
      }
    };
  };

  const handleMessageClick = (e) => {
    // Check if Ctrl (Windows/Linux) or Cmd (Mac) is pressed
    const isModifierPressed = e?.ctrlKey || e?.metaKey;
    
    if (isModifierPressed) {
      // Open in new tab
      e?.preventDefault();
      if (!isOwnProfile && isAuthenticated && user?.id) {
        const url = `${window.location.origin}/messages`;
        window.open(url, '_blank', 'noopener,noreferrer');
      } else if (!isAuthenticated) {
        const url = `${window.location.origin}/login`;
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } else {
      // Normal navigation
      if (!isOwnProfile && isAuthenticated && user?.id) {
        navigate('/messages', { state: { userId: user.id, userName: user.name } });
      } else if (!isAuthenticated) {
        navigate('/login');
      }
    }
  };

  const generatePDF = async () => {
    try {
      setGeneratingPDF(true);
      
      console.log('Fetching resume HTML from backend...');
      
      // Fetch HTML template from backend API
      const response = await ApiService.get(`/resume-html/${username}`);
      
      console.log('Backend response:', response);
      
      if (response.success) {
        const htmlContent = response.data.html;
        const filename = response.data.filename;
        
        console.log('HTML template received from backend');
        console.log('HTML content length:', htmlContent.length);
        
        // Open HTML in new tab for printing
        const printWindow = window.open('', '_blank');
        
        if (printWindow) {
          printWindow.document.write(htmlContent);
          printWindow.document.close();
          
          // Wait for content to load, then trigger print dialog
          printWindow.onload = () => {
            setTimeout(() => {
              printWindow.print();
            }, 500);
          };
          
          console.log('Resume opened in new tab for printing');
        } else {
          throw new Error('Failed to open print tab. Please check popup blockers.');
        }
      } else {
        console.error('Backend error:', response.message);
        throw new Error(response.message || 'Failed to fetch resume HTML');
      }

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to open resume for printing. Please try again.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const getAchievementBadge = () => {
    if (!user) return null;

    const hasProjects = user.projects && user.projects.length > 0;
    const hasExperience = user.experiences && user.experiences.length > 0;
    const hasSkills = user.skills && user.skills.length > 0;
    const hasEducation = user.education && user.education.length > 0;

    if (hasExperience) {
      return (
        <div className="achievement-badge legend-badge">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
            <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
            <path d="M4 22h16"></path>
            <path d="M10 14.66V17c0 .55-.47.98-.97 1.21l-1.25.5c-.3.12-.5.42-.5.74 0 .32.2.62.5.74l1.25.5c.5.23.97.66.97 1.21v2.34"></path>
            <path d="M14 14.66V17c0 .55.47.98.97 1.21l1.25.5c.3.12.5.42.5.74 0 .32-.2.62-.5.74l-1.25.5c-.5.23-.97.66-.97 1.21v2.34"></path>
            <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
          </svg>
          <span>Legend</span>
        </div>
      );
    }
    if (hasProjects) {
      return (
        <div className="achievement-badge shining-star-badge">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"></polygon>
          </svg>
          <span>Shining Star</span>
        </div>
      );
    }
    if (hasSkills || hasEducation) {
      return (
        <div className="achievement-badge rising-star-badge">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
          </svg>
          <span>Rising Star</span>
        </div>
      );
    }
    return (
      <div className="achievement-badge newcomer-badge">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M12 6v6l4 2"></path>
        </svg>
        <span>Newcomer</span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Present';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short'
    });
  };


  // Navigation handlers for Header with Ctrl+Click support
  const handleLoginClick = (e) => {
    const isModifierPressed = e?.ctrlKey || e?.metaKey;
    if (isModifierPressed) {
      e?.preventDefault();
      window.open(`${window.location.origin}/login`, '_blank', 'noopener,noreferrer');
    } else {
      navigate('/login');
    }
  };

  const handleSignupClick = (e) => {
    const isModifierPressed = e?.ctrlKey || e?.metaKey;
    if (isModifierPressed) {
      e?.preventDefault();
      window.open(`${window.location.origin}/signup`, '_blank', 'noopener,noreferrer');
    } else {
      navigate('/signup');
    }
  };

  const handleHomeClick = (e) => {
    const isModifierPressed = e?.ctrlKey || e?.metaKey;
    if (isModifierPressed) {
      e?.preventDefault();
      window.open(`${window.location.origin}/`, '_blank', 'noopener,noreferrer');
    } else {
      navigate('/');
    }
  };

  const handleCategoriesClick = (e) => {
    const isModifierPressed = e?.ctrlKey || e?.metaKey;
    if (isModifierPressed) {
      e?.preventDefault();
      window.open(`${window.location.origin}/#categories`, '_blank', 'noopener,noreferrer');
    } else {
      navigate('/#categories');
    }
  };

  const handleHowItWorksClick = (e) => {
    const isModifierPressed = e?.ctrlKey || e?.metaKey;
    if (isModifierPressed) {
      e?.preventDefault();
      window.open(`${window.location.origin}/#how-it-works`, '_blank', 'noopener,noreferrer');
    } else {
      navigate('/#how-it-works');
    }
  };

  const handleProfileClick = (e) => {
    const isModifierPressed = e?.ctrlKey || e?.metaKey;
    if (isModifierPressed) {
      e?.preventDefault();
      window.open(`${window.location.origin}/profile`, '_blank', 'noopener,noreferrer');
    } else {
      navigate('/profile');
    }
  };

  const handleAccountSettingsClick = (e) => {
    const isModifierPressed = e?.ctrlKey || e?.metaKey;
    if (isModifierPressed) {
      e?.preventDefault();
      window.open(`${window.location.origin}/settings`, '_blank', 'noopener,noreferrer');
    } else {
      navigate('/settings');
    }
  };

  const handleForumClick = (e) => {
    const isModifierPressed = e?.ctrlKey || e?.metaKey;
    if (isModifierPressed) {
      e?.preventDefault();
      window.open(`${window.location.origin}/forum`, '_blank', 'noopener,noreferrer');
    } else {
      navigate('/forum');
    }
  };

  const handleJobsClick = (e) => {
    const isModifierPressed = e?.ctrlKey || e?.metaKey;
    if (isModifierPressed) {
      e?.preventDefault();
      window.open(`${window.location.origin}/vacancies`, '_blank', 'noopener,noreferrer');
    } else {
      navigate('/vacancies');
    }
  };

  const handleMessagesClick = (e) => {
    const isModifierPressed = e?.ctrlKey || e?.metaKey;
    if (isModifierPressed) {
      e?.preventDefault();
      window.open(`${window.location.origin}/messages`, '_blank', 'noopener,noreferrer');
    } else {
      // Messages feature placeholder
      console.log('Messages clicked');
    }
  };

  if (loading) {
    return (
      <div className="public-profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="public-profile-error">
        <div className="error-content">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <h2>Profile Not Found</h2>
          <p>The profile you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-profile-container public-profile public-profile-with-bottom-nav">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{user ? `${user.name} - Professional Profile` : 'Loading...'}</title>
        <meta name="description" content={user?.bio || `${user?.name} - Professional profile showcasing skills, experience, and projects.`} />
        <meta name="keywords" content={`${user?.name}, professional, portfolio, skills, experience, projects, ${user?.skills?.map(s => s.name).join(', ')}`} />
        <meta property="og:title" content={`${user?.name} - Professional Profile`} />
        <meta property="og:description" content={user?.bio || `${user?.name} - Professional profile showcasing skills, experience, and projects.`} />
        <meta property="og:image" content={user?.profile_photo_url || user?.banner_image_url} />
        <meta property="og:url" content={`${window.location.origin}/talent/${username}`} />
        <meta property="og:type" content="profile" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${user?.name} - Professional Profile`} />
        <meta name="twitter:description" content={user?.bio || `${user?.name} - Professional profile showcasing skills, experience, and projects.`} />
        <meta name="twitter:image" content={user?.profile_photo_url || user?.banner_image_url} />
        <meta name="twitter:site" content="@RemoteBirdy" />
        <link rel="canonical" href={`${window.location.origin}/talent/${username}`} />
      </Helmet>

      {/* Platform Header - Hidden on mobile */}
      <div className="public-profile-header">
          <Header 
            onLoginClick={handleLoginClick}
            onSignupClick={handleSignupClick}
            isLoggedIn={isAuthenticated}
            user={currentUser}
            onHomeClick={handleHomeClick}
            onCategoriesClick={handleCategoriesClick}
            onHowItWorksClick={handleHowItWorksClick}
            onProfileClick={handleProfileClick}
            onAccountSettingsClick={handleAccountSettingsClick}
            currentPage="public-profile"
          />
      </div>

      {/* Hidden User Alert - Show to user who hid the user - Positioned after navigation bar */}
      {isAuthenticated && isHidden && !isOwnProfile && (
        <div className="job-hidden-alert" style={{ margin: '0 auto', borderRadius: '0', maxWidth: '1200px', width: '100%' }}>
          <div className="alert-content" style={{ padding: '16px 20px' }}>
            <div className="alert-text">
              <h4>This user is hidden for you</h4>
              <p>You previously hid this user. You can unhide them to see them in your listings again.</p>
            </div>
            <button 
              onClick={handleUnhideUser}
              className="unhide-job-btn"
              disabled={unhiding}
            >
              {unhiding ? (
                <>
                  <div className="spinner-small"></div>
                  Unhiding...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  Unhide User
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* LinkedIn-style Profile Layout */}
      <div className="linkedin-profile">
        {/* Cover Photo Section */}
        <div className="cover-photo-section">
          <div className="cover-photo">
            {user.banner_image_url ? (
              <img 
                src={user.banner_image_url} 
                alt={`${user.name} cover photo`} 
                className="cover-image"
                loading="eager"
                decoding="async"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="cover-placeholder">
                <div className="cover-pattern"></div>
                <div className="cover-placeholder-content">
                  <div className="cover-placeholder-text">
                    <h4>Make a Powerful First Impression</h4>
                    <p>A professional banner image helps you stand out and showcases your personal brand. Add a cover photo that reflects your expertise and attracts opportunities.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Profile Picture */}
          <div className="profile-picture-section">
            <div className="profile-picture-container">
              {user.profile_photo_url ? (
                <img 
                  src={user.profile_photo_url} 
                  alt={`${user.name} profile picture`} 
                  className="profile-picture"
                  loading="eager"
                  decoding="async"
                />
              ) : (
                <div className="profile-picture-placeholder">
                  <span>{user.name?.charAt(0)?.toUpperCase()}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Profile Header */}
        <div className="profile-header-section">
          {/* Print Resume Button - Top Right */}
          <div className="achievement-badge-corner print-resume-corner">
            <button className="action-button secondary" onClick={generatePDF} disabled={generatingPDF}>
              {generatingPDF ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin">
                    <path d="M21 12a9 9 0 11-6.219-8.56"/>
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                  </svg>
                  Print Resume
                </>
              )}
            </button>
          </div>

          <div className="profile-header-content">
            <div className="profile-info">
              {/* Achievement Badge - Right of Name/Title */}
              <div className="achievement-badge-corner legend-badge-corner">
                {getAchievementBadge()}
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                justifyContent: 'space-between', 
                gap: '16px', 
                marginBottom: '12px', 
                flexWrap: 'wrap', 
                width: '100%'
              }}>
                <div className="name-title-container" style={{ 
                  flex: '1 1 auto', 
                  minWidth: '200px',
                  paddingRight: '16px'
                }}>
                  <h1 className="profile-name">
                    {user.name}
                    {user.professional_title && (
                      <span className="name-title-separator"> - </span>
                    )}
                    {user.professional_title && (
                      <span className="professional-title-inline">{user.professional_title}</span>
                    )}
                  </h1>
                  {!user.professional_title && (
                    <div className="professional-title-placeholder">
                      <span>Professional</span>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons - Message, Save, Report */}
                {!isOwnProfile && (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    flexShrink: 0,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end'
                  }}
                  className="talent-action-buttons"
                  >
                    {/* Chat/Message Button */}
                    <button 
                      onClick={(e) => {
                        if (isOwnProfile) {
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }
                        handleMessageClick(e);
                      }}
                      disabled={isOwnProfile}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '40px',
                        height: '40px',
                        padding: 0,
                        background: isOwnProfile ? '#f3f4f6' : 'transparent',
                        color: isOwnProfile ? '#9ca3af' : '#7a5fb8',
                        border: `1px solid ${isOwnProfile ? '#d1d5db' : '#7a5fb8'}`,
                        borderRadius: '6px',
                        cursor: isOwnProfile ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        flexShrink: 0,
                        opacity: isOwnProfile ? 0.6 : 1,
                        pointerEvents: isOwnProfile ? 'none' : 'auto'
                      }}
                      onMouseEnter={(e) => {
                        if (!isOwnProfile) {
                          e.currentTarget.style.background = '#f5f3ff';
                          e.currentTarget.style.borderColor = '#6b4fa0';
                          e.currentTarget.style.color = '#6b4fa0';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isOwnProfile) {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.borderColor = '#7a5fb8';
                          e.currentTarget.style.color = '#7a5fb8';
                        }
                      }}
                      title={isOwnProfile ? "Cannot chat with yourself" : "Message this user"}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                      </svg>
                    </button>
                    
                    {/* Save/Bookmark Button - Blue */}
                    <button 
                      onClick={handleBookmarkClick}
                      disabled={isOwnProfile || !isAuthenticated}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '40px',
                        height: '40px',
                        padding: 0,
                        background: (isOwnProfile || !isAuthenticated) ? '#f3f4f6' : (isBookmarked ? '#e0f2fe' : 'transparent'),
                        color: (isOwnProfile || !isAuthenticated) ? '#9ca3af' : (isBookmarked ? '#0369a1' : '#0073b1'),
                        border: `1px solid ${(isOwnProfile || !isAuthenticated) ? '#d1d5db' : (isBookmarked ? '#0369a1' : '#0073b1')}`,
                        borderRadius: '6px',
                        cursor: (isOwnProfile || !isAuthenticated) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        flexShrink: 0,
                        opacity: (isOwnProfile || !isAuthenticated) ? 0.6 : 1,
                        pointerEvents: (isOwnProfile || !isAuthenticated) ? 'none' : 'auto'
                      }}
                      onMouseEnter={(e) => {
                        if (!isOwnProfile && isAuthenticated) {
                          e.currentTarget.style.background = '#f0f7fa';
                          e.currentTarget.style.borderColor = '#006ba1';
                          e.currentTarget.style.color = '#006ba1';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isOwnProfile && isAuthenticated) {
                          e.currentTarget.style.background = isBookmarked ? '#e0f2fe' : 'transparent';
                          e.currentTarget.style.borderColor = isBookmarked ? '#0369a1' : '#0073b1';
                          e.currentTarget.style.color = isBookmarked ? '#0369a1' : '#0073b1';
                        }
                      }}
                      title={isOwnProfile ? "Cannot save your own profile" : (isBookmarked ? "Already bookmarked" : "Save user for future consideration")}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        {isBookmarked ? (
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" fill="currentColor"></path>
                        ) : (
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                        )}
                      </svg>
                    </button>
                    
                    {/* Report/Unhide Button */}
                    {isHidden ? (
                      // If user is hidden, show "Unhide User" button instead of "Report" button
                      <button 
                        onClick={handleUnhideUser}
                        className="unhide-button-inline"
                        title="Unhide this user"
                        disabled={unhiding}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 16px',
                          background: 'transparent',
                          color: '#7a5fb8',
                          border: '1px solid #7a5fb8',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: 600,
                          cursor: unhiding ? 'not-allowed' : 'pointer',
                          transition: 'all 0.2s ease',
                          whiteSpace: 'nowrap',
                          opacity: unhiding ? 0.6 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!unhiding) {
                            e.currentTarget.style.background = '#f5f3ff';
                            e.currentTarget.style.borderColor = '#6b4fa0';
                            e.currentTarget.style.color = '#6b4fa0';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!unhiding) {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.borderColor = '#7a5fb8';
                            e.currentTarget.style.color = '#7a5fb8';
                          }
                        }}
                      >
                        {unhiding ? (
                          <>
                            <div className="spinner-small"></div>
                            Unhiding...
                          </>
                        ) : (
                          <>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                            Unhide User
                          </>
                        )}
                      </button>
                    ) : (
                      // Report Button - Red/Maroon
                    <button 
                      onClick={(e) => {
                        if (isOwnProfile) {
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }
                        setShowReportModal(true);
                      }}
                      disabled={isOwnProfile}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '40px',
                        height: '40px',
                        padding: 0,
                        background: isOwnProfile ? '#f3f4f6' : 'transparent',
                        color: isOwnProfile ? '#9ca3af' : '#991b1b',
                        border: `1px solid ${isOwnProfile ? '#d1d5db' : '#991b1b'}`,
                        borderRadius: '6px',
                        cursor: isOwnProfile ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        flexShrink: 0,
                        opacity: isOwnProfile ? 0.6 : 1,
                        pointerEvents: isOwnProfile ? 'none' : 'auto'
                      }}
                      onMouseEnter={(e) => {
                        if (!isOwnProfile) {
                          e.currentTarget.style.background = '#fef2f2';
                          e.currentTarget.style.borderColor = '#7f1d1d';
                          e.currentTarget.style.color = '#7f1d1d';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isOwnProfile) {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.borderColor = '#991b1b';
                          e.currentTarget.style.color = '#991b1b';
                        }
                      }}
                      title={isOwnProfile ? "Cannot report your own profile" : "Report this profile"}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                    </button>
                    )}
                  </div>
                )}
              </div>
              
              {(user.work_type || user.job_seeking_status) && (
                <div className="profile-details-section">
                  {user.job_seeking_status && (
                    <span className={`job-seeking-status ${user.job_seeking_status === 'actively-seeking' ? 'actively-seeking' : ''}`}>
                      {user.job_seeking_status === 'actively-seeking' ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-status={user.job_seeking_status}>
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                          <path d="M8 12l2 2 4-4"></path>
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-status={user.job_seeking_status}>
                          <path d="M9 12l2 2 4-4"></path>
                          <path d="M21 12c.552 0 1-.448 1-1V5c0-.552-.448-1-1-1H3c-.552 0-1 .448-1 1v6c0 .552.448 1 1 1h18z"></path>
                          <path d="M3 12v6c0 .552.448 1 1 1h16c.552 0 1-.448 1-1v-6"></path>
                        </svg>
                      )}
                      {user.job_seeking_status === 'actively-seeking' ? (
                        <span className="status-text">
                          Actively Seeking
                        </span>
                      ) : (
                        <span className="status-text">
                          {user.job_seeking_status.charAt(0).toUpperCase() + user.job_seeking_status.slice(1).replace(/-/g, ' ')}
                        </span>
                      )}
                    </span>
                  )}
                  
                  {user.work_type && (
                    <span className="work-preference">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                        <line x1="8" y1="21" x2="16" y2="21"></line>
                        <line x1="12" y1="17" x2="12" y2="21"></line>
                      </svg>
                      {user.work_type.charAt(0).toUpperCase() + user.work_type.slice(1).replace('-', ' ')}
                    </span>
                  )}
                </div>
              )}
              
              {/* Followers/Following Stats and Follow Button - Hidden for now, will be enabled in future
              {isAuthenticated && currentUser && currentUser.id !== user.id && (
                <div className="profile-social-section">
                  <UserFollowStats 
                    userId={user.id}
                    followersCount={user.followers_count || 0}
                    followingCount={user.following_count || 0}
                  />
                  <FollowButton userId={user.id} />
                </div>
              )}
              
              {(!isAuthenticated || !currentUser || currentUser.id === user.id) && (
                <div className="profile-social-section">
                  <UserFollowStats 
                    userId={user.id}
                    followersCount={user.followers_count || 0}
                    followingCount={user.following_count || 0}
                  />
                </div>
              )}
              */}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="profile-main-content">
          <div className="content-layout">
            {/* Left Sidebar */}
            <div className="profile-sidebar">
              {/* Contact Info */}
              <div className="sidebar-card">
                <h3>Contact Information</h3>
                <div className="contact-info">
                  <div className="contact-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    <span>{user.email}</span>
                  </div>
                  {user.phone && (
                    <div className="contact-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                      <span>{user.phone}</span>
                    </div>
                  )}
                  {user.timezone_display && (
                    <div className="contact-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12,6 12,12 16,14"></polyline>
                      </svg>
                      <span>{user.timezone_display}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Skills */}
              <div className="sidebar-card">
                <h3>Skills</h3>
                <div className="skills-list">
                  {user.skills && user.skills.length > 0 ? (
                    <>
                      {user.skills.slice(0, 10).map((skill, index) => (
                        <div key={index} className="skill-tag">
                          {skill?.name || 'Unknown Skill'}
                        </div>
                      ))}
                      {user.skills.length > 10 && (
                        <div className="skill-tag more">
                          +{user.skills.length - 10} more
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="placeholder-content-small">
                      <div className="placeholder-icon-small">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                          <path d="M2 17l10 5 10-5"></path>
                          <path d="M2 12l10 5 10-5"></path>
                        </svg>
                      </div>
                      <p className="placeholder-text-small">Skills showcase coming soon</p>
                      <p className="placeholder-subtext-small">Add your expertise to highlight your capabilities</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="profile-main">
              {/* Main Content */}
              <div className="main-content">
                {/* Professional Summary */}
                <div className="content-card">
                  <h3>About</h3>
                  <div className="summary-content">
                    {user.bio ? (
                      <p>{user.bio}</p>
                    ) : (
                      <div className="placeholder-content">
                        <div className="placeholder-icon">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                          </svg>
                        </div>
                        <p className="placeholder-text">Share Your Professional Story</p>
                        <p className="placeholder-subtext">A compelling summary helps visitors understand your expertise, achievements, and what sets you apart. Add your professional summary to make a strong first impression and showcase your unique value proposition to potential employers or collaborators.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Work Experience */}
                <div className="content-card">
                  <h3>Experience</h3>
                  <div className="public-profile experience-list">
                    {user.experiences && user.experiences.length > 0 ? (
                      user.experiences.map((exp, index) => (
                        <div key={index} className="experience-item">
                          <div className="experience-header">
                            <h4>{exp?.job_title || 'Unknown Position'}</h4>
                            <span className="experience-company">{exp?.company_name || 'Unknown Company'}</span>
                          </div>
                          <div className="experience-details">
                            <span className="experience-duration">
                              {formatDate(exp?.start_date)} - {formatDate(exp?.end_date)}
                            </span>
                            {exp?.location && (
                              <div className="experience-location">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                  <circle cx="12" cy="10" r="3"></circle>
                                </svg>
                                <span>{exp.location}</span>
                              </div>
                            )}
                          </div>
                          {exp?.description && (
                            <p className="experience-description">{exp.description}</p>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="placeholder-content">
                        <div className="placeholder-icon">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                          </svg>
                        </div>
                        <p className="placeholder-text">Build Your Professional Journey</p>
                        <p className="placeholder-subtext">Your work experience is one of the most important aspects of your profile. Adding your professional history demonstrates your career growth, industry expertise, and the value you bring to organizations. Complete your experience section to showcase your career achievements and attract better opportunities.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Projects */}
                <div className="content-card">
                  <h3>Projects</h3>
                  <div className="projects-list">
                    {user.projects && user.projects.length > 0 ? (
                      user.projects.map((project, index) => (
                        <div key={index} className="project-item">
                          <div className="project-header">
                            <h4>{project?.title || 'Untitled Project'}</h4>
                            {project?.status && (
                              <span className={`project-status ${project.status.toLowerCase()}`}>
                                {project.status}
                              </span>
                            )}
                          </div>
                          {project?.description && (
                            <p className="project-description">{project.description}</p>
                          )}
                          {project?.technologies && Array.isArray(project.technologies) && project.technologies.length > 0 && (
                            <div className="project-technologies">
                              {project.technologies.map((tech, techIndex) => (
                                <span key={techIndex} className="tech-tag">{tech}</span>
                              ))}
                            </div>
                          )}
                          {project?.project_url && (
                            <a href={project.project_url} target="_blank" rel="noopener noreferrer" className="project-link">
                              View Project
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                <polyline points="15,3 21,3 21,9"></polyline>
                                <line x1="10" y1="14" x2="21" y2="3"></line>
                              </svg>
                            </a>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="placeholder-content">
                        <div className="placeholder-icon">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                            <polyline points="2 17 12 22 22 17"></polyline>
                            <polyline points="2 12 12 17 22 12"></polyline>
                          </svg>
                        </div>
                        <p className="placeholder-text">Showcase Your Best Work</p>
                        <p className="placeholder-subtext">Projects are the proof of your skills and capabilities. Displaying your portfolio demonstrates your technical expertise, problem-solving abilities, and the real-world impact of your work. Add your projects to give visitors concrete examples of what you can achieve and stand out from the crowd.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Education */}
                <div className="content-card">
                  <h3>Education</h3>
                  <div className="education-list">
                    {user.education && user.education.length > 0 ? (
                      user.education.map((edu, index) => (
                        <div key={index} className="education-item">
                          <div className="education-header">
                            <h4>
                              {edu?.degree || 'Degree'}
                              {edu?.field_of_study && ` (${edu.field_of_study})`} - {edu?.institution || 'Institution'}
                              <span className="education-duration">
                                {formatDate(edu?.start_date)} - {formatDate(edu?.end_date)}
                              </span>
                            </h4>
                          </div>
                          {edu?.description && (
                            <p className="education-description">{edu.description}</p>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="placeholder-content">
                        <div className="placeholder-icon">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                            <line x1="9" y1="7" x2="15" y2="7"></line>
                            <line x1="9" y1="11" x2="15" y2="11"></line>
                          </svg>
                        </div>
                        <p className="placeholder-text">Highlight Your Educational Foundation</p>
                        <p className="placeholder-subtext">Your educational background demonstrates your commitment to learning and your foundation of knowledge. Completing this section adds credibility to your profile and helps employers understand your academic qualifications. Share your educational journey to build trust and showcase your dedication to professional development.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Reviews Section */}
                {user?.id && (
                  <ReviewsList 
                    userId={user.id}
                    currentUserId={currentUser?.id}
                    isOwnProfile={isOwnProfile}
                  />
                )}

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Footer */}
      <Footer />

      {/* Bottom Navigation Bar - Visible only on mobile */}
      <BottomNavigationBar 
        onHomeClick={handleHomeClick}
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
        onProfileClick={handleProfileClick}
        onAccountSettingsClick={handleAccountSettingsClick}
        onJobsClick={handleJobsClick}
        onMessagesClick={handleMessagesClick}
        onForumClick={handleForumClick}
      />

      {/* Bookmark Modal */}
      {isAuthenticated && !isOwnProfile && (
        <BookmarkModal
          isOpen={showBookmarkModal}
          onClose={() => setShowBookmarkModal(false)}
          talentId={user?.id}
          talentName={user?.name}
          onBookmarked={handleBookmarked}
        />
      )}

      {/* Report User Modal */}
      {isAuthenticated && !isOwnProfile && (
        <ReportUserModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          userId={user?.id}
          userName={user?.name}
        />
      )}
    </div>
  );
};

export default PublicProfile;
